import React from 'react';
import { X, Crown, CalendarDays, ShieldOff, Zap, Bot, AlertCircle, Loader } from 'lucide-react';
import { formatDate, getDaysBetween, calculatePrice, getPlanPrice } from '../utils/formatters';

const SubscriptionModal = ({ 
  isOpen, 
  selectedUser, 
  subscriptionAction,
  subscriptionType,
  subscriptionDuration,
  customStartDate,
  customEndDate,
  showDatePicker,
  loading,
  onClose,
  onSetSubscriptionType,
  onSetSubscriptionDuration,
  onSetCustomStartDate,
  onSetCustomEndDate,
  onSetShowDatePicker,
  onSubmit,
  formatDateForInput
}) => {
  if (!isOpen || !selectedUser) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[200] backdrop-blur-sm">
      <div className="bg-gray-900 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl border border-gray-700">
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between bg-gradient-to-r from-gray-800 to-gray-900">
          <div className="flex items-center gap-2">
            {subscriptionAction === 'grant' && <Crown className="w-5 h-5 text-yellow-400" />}
            {subscriptionAction === 'extend' && <CalendarDays className="w-5 h-5 text-blue-400" />}
            {subscriptionAction === 'revoke' && <ShieldOff className="w-5 h-5 text-red-400" />}
            <h2 className="text-lg font-semibold text-white">
              {subscriptionAction === 'grant' && 'Выдача Premium доступа'}
              {subscriptionAction === 'extend' && 'Продление подписки'}
              {subscriptionAction === 'revoke' && 'Отзыв Premium доступа'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-300 hover:bg-gray-800 rounded transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {subscriptionAction !== 'revoke' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">Тип активации</label>
                <div className="grid grid-cols-2 gap-3">
                  <button 
                    onClick={() => onSetSubscriptionType('auto')} 
                    className={`flex flex-col items-center p-3 rounded-lg border transition-colors ${
                      subscriptionType === 'auto' 
                        ? 'bg-blue-900/30 border-blue-700' 
                        : 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                    }`}
                  >
                    <Zap className={`w-5 h-5 mb-1 ${subscriptionType === 'auto' ? 'text-blue-400' : 'text-gray-400'}`} />
                    <span className={`text-xs font-medium ${subscriptionType === 'auto' ? 'text-blue-300' : 'text-gray-300'}`}>Авто (Stripe)</span>
                    <span className="text-xs text-gray-500 mt-1">Оплачено через систему</span>
                  </button>
                  <button 
                    onClick={() => onSetSubscriptionType('manual')} 
                    className={`flex flex-col items-center p-3 rounded-lg border transition-colors ${
                      subscriptionType === 'manual' 
                        ? 'bg-purple-900/30 border-purple-700' 
                        : 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                    }`}
                  >
                    <Bot className={`w-5 h-5 mb-1 ${subscriptionType === 'manual' ? 'text-purple-400' : 'text-gray-400'}`} />
                    <span className={`text-xs font-medium ${subscriptionType === 'manual' ? 'text-purple-300' : 'text-gray-300'}`}>Ручная</span>
                    <span className="text-xs text-gray-500 mt-1">Активация администратором</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">Период подписки</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  <button 
                    onClick={() => {
                      onSetSubscriptionDuration('1');
                      onSetShowDatePicker(false);
                      const now = new Date();
                      const endDate = new Date();
                      endDate.setMonth(endDate.getMonth() + 1);
                      onSetCustomStartDate(formatDateForInput(now));
                      onSetCustomEndDate(formatDateForInput(endDate));
                    }} 
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      subscriptionDuration === '1' 
                        ? 'bg-green-900/30 text-green-300 border border-green-700' 
                        : 'bg-gray-800 text-gray-300 border border-gray-700 hover:bg-gray-700'
                    }`}
                  >
                    1 месяц
                  </button>
                  <button 
                    onClick={() => {
                      onSetSubscriptionDuration('3');
                      onSetShowDatePicker(false);
                      const now = new Date();
                      const endDate = new Date();
                      endDate.setMonth(endDate.getMonth() + 3);
                      onSetCustomStartDate(formatDateForInput(now));
                      onSetCustomEndDate(formatDateForInput(endDate));
                    }} 
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      subscriptionDuration === '3' 
                        ? 'bg-blue-900/30 text-blue-300 border border-blue-700' 
                        : 'bg-gray-800 text-gray-300 border border-gray-700 hover:bg-gray-700'
                    }`}
                  >
                    3 месяца
                  </button>
                  <button 
                    onClick={() => {
                      onSetSubscriptionDuration('6');
                      onSetShowDatePicker(false);
                      const now = new Date();
                      const endDate = new Date();
                      endDate.setMonth(endDate.getMonth() + 6);
                      onSetCustomStartDate(formatDateForInput(now));
                      onSetCustomEndDate(formatDateForInput(endDate));
                    }} 
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      subscriptionDuration === '6' 
                        ? 'bg-purple-900/30 text-purple-300 border border-purple-700' 
                        : 'bg-gray-800 text-gray-300 border border-gray-700 hover:bg-gray-700'
                    }`}
                  >
                    6 месяцев
                  </button>
                  <button 
                    onClick={() => {
                      onSetSubscriptionDuration('custom');
                      onSetShowDatePicker(!showDatePicker);
                    }} 
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      subscriptionDuration === 'custom' 
                        ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-700' 
                        : 'bg-gray-800 text-gray-300 border border-gray-700 hover:bg-gray-700'
                    }`}
                  >
                    <CalendarDays className="w-4 h-4 inline mr-1" />
                    Свой период
                  </button>
                </div>

                {showDatePicker && subscriptionDuration === 'custom' && (
                  <div className="space-y-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Дата начала</label>
                      <input 
                        type="date" 
                        value={customStartDate} 
                        onChange={(e) => onSetCustomStartDate(e.target.value)} 
                        min={formatDateForInput(new Date())} 
                        className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Дата окончания</label>
                      <input 
                        type="date" 
                        value={customEndDate} 
                        onChange={(e) => onSetCustomEndDate(e.target.value)} 
                        min={customStartDate || formatDateForInput(new Date())} 
                        className="w-full px-4 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" 
                      />
                    </div>
                    {customStartDate && customEndDate && (
                      <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-400">Длительность:</span>
                          <span className="text-sm font-medium text-blue-300">
                            {getDaysBetween(customStartDate, customEndDate)} дней
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-400">Стоимость:</span>
                          <span className="text-lg font-bold text-white">
                            {calculatePrice(getDaysBetween(customStartDate, customEndDate)).toLocaleString()} €
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedUser.premium && subscriptionAction === 'extend' && (
                <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-blue-300">Текущая подписка истекает: {formatDate(selectedUser.premiumExpiresAt)}</p>
                      <p className="text-xs text-gray-400 mt-1">Подписка будет продлена от текущей даты окончания</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-400">Пользователь:</span>
                  <span className="text-sm font-medium text-white">
                    {selectedUser.fullName || selectedUser.username || selectedUser.id.substring(0, 8)}
                  </span>
                </div>
                {subscriptionDuration !== 'custom' ? (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">Сумма:</span>
                    <span className="text-lg font-bold text-white">{getPlanPrice(subscriptionDuration).toLocaleString()} €</span>
                  </div>
                ) : (customStartDate && customEndDate && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-400">Сумма:</span>
                    <span className="text-lg font-bold text-white">
                      {calculatePrice(getDaysBetween(customStartDate, customEndDate)).toLocaleString()} €
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <ShieldOff className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">Отозвать Premium доступ</h3>
              <p className="text-sm text-gray-400 mb-4">
                Пользователь потеряет доступ ко всем Premium функциям. Действие можно отменить, выдав подписку заново.
              </p>
              <div className="bg-red-900/30 border border-red-700 rounded-lg p-3">
                <p className="text-xs text-red-300">
                  Пользователь: {selectedUser.fullName || selectedUser.username || selectedUser.id.substring(0, 8)}
                </p>
                {selectedUser.premiumExpiresAt && (
                  <p className="text-xs text-gray-400 mt-1">
                    Подписка активна до: {formatDate(selectedUser.premiumExpiresAt)}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-gray-700">
            <button 
              onClick={onSubmit} 
              disabled={loading || (subscriptionDuration === 'custom' && (!customStartDate || !customEndDate))} 
              className={`flex-1 px-4 py-3 font-medium rounded-lg transition-colors flex items-center justify-center gap-2 ${
                subscriptionAction === 'revoke' 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Обработка...
                </>
              ) : (
                <>
                  {subscriptionAction === 'grant' && 'Выдать Premium'}
                  {subscriptionAction === 'extend' && 'Продлить подписку'}
                  {subscriptionAction === 'revoke' && 'Отозвать доступ'}
                </>
              )}
            </button>
            <button onClick={onClose} className="px-4 py-3 bg-gray-700 text-gray-300 font-medium rounded-lg hover:bg-gray-600 transition-colors">
              Отмена
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;